import { useCallback, useEffect, useRef } from 'react';
import {
  fetchAcpAgentOptions,
  filterSelectedAcpAgents,
  mergeSelectedAcpAgents,
} from '@/lib/agent-runtime/acp-agent-catalog';
import { applyAgentRuntimeOverrides } from '@/lib/agent-runtime/agent-runtime-config';
import { selectElectronAgentId } from '@/lib/agent-runtime/agent-runtime-options';
import type { AppSettings } from '@/lib/app-settings';
import { loadPinnedConversations, seedPinnedConversations } from '@/lib/conversation-pins';
import {
  PREVIEW_AGENT,
  PREVIEW_PINNED_CONVERSATIONS,
  PREVIEW_WORKSPACE_OPENERS,
} from '@/lib/desktop-preview-data';
import { preferredAgentCommand } from '@/lib/desktop-shell/cloud-agents';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { PERSONAL_WORKSPACE_KEY, WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import {
  discoverNativeAgents,
  ensureNativeManagedWorkspace,
  listNativeWorkspaceOpeners,
} from '@/lib/native-desktop';

/** Owns native/cloud runtime discovery and the initial desktop workspace bootstrap. */
export function useDesktopRuntimeBootstrap({
  store,
  appSettings,
  initialWorkspace,
  usePersonalWorkspace,
  preview,
  previewSetup,
}: {
  store: DesktopShellStore;
  appSettings: AppSettings;
  initialWorkspace?: string;
  usePersonalWorkspace?: boolean;
  preview: boolean;
  previewSetup: 'workspace' | null;
}): { refreshAgents(): Promise<void> } {
  const agentDiscoveryRequestRef = useRef(0);
  const refreshAgents = useCallback(async (): Promise<void> => {
    const request = ++agentDiscoveryRequestRef.current;
    const { setDiscoveringAgents, setAgentDiscoveryError, setAgents, setAgentId } =
      store.getState();
    setDiscoveringAgents(true);
    try {
      const [discovered, registryAgents] = await Promise.all([
        discoverNativeAgents(),
        fetchAcpAgentOptions().catch(() => []),
      ]);
      if (request !== agentDiscoveryRequestRef.current) return;
      const found = mergeSelectedAcpAgents(
        applyAgentRuntimeOverrides(discovered, appSettings.agentRuntimeOverrides),
        registryAgents,
        appSettings.agentIds,
      );
      setAgentDiscoveryError(null);
      setAgents(found);
      setAgentId((current) => selectElectronAgentId(found, current, appSettings.defaultAgent));
    } catch (reason) {
      if (request !== agentDiscoveryRequestRef.current) return;
      setAgentDiscoveryError(errorMessage(reason));
    } finally {
      if (request === agentDiscoveryRequestRef.current) setDiscoveringAgents(false);
    }
  }, [appSettings.agentIds, appSettings.agentRuntimeOverrides, appSettings.defaultAgent, store]);

  useEffect(() => {
    // Invalidate discovery started by the previous runtime/bootstrap state. This
    // prevents a late native response from replacing preview or cloud agents.
    agentDiscoveryRequestRef.current += 1;
    let cancelled = false;
    const {
      setNative,
      setWorkspace,
      setAgents,
      setAgentId,
      setDiscoveringAgents,
      setWorkspaceOpeners,
      setPreferredOpenerId,
      setAgentDiscoveryError,
      setWorkspaceProvisionError,
      setExecutionMode,
    } = store.getState();
    if (preview) {
      setNative(true);
      setWorkspace(previewSetup === 'workspace' ? null : '/Users/jonas/Projects/crowdex');
      setAgents([PREVIEW_AGENT]);
      setAgentId(PREVIEW_AGENT.id);
      setDiscoveringAgents(false);
      setWorkspaceOpeners(PREVIEW_WORKSPACE_OPENERS);
      setPreferredOpenerId('vscode');
      if (new URLSearchParams(window.location.search).has('pinned'))
        seedPinnedConversations(PREVIEW_PINNED_CONVERSATIONS);
      return;
    }

    // The server's pins: the sidebar's, and what worktree cleanup keeps.
    void loadPinnedConversations().catch(() => undefined);
    const detected = isLocalMachine();
    setNative(detected);
    if (!detected) {
      setExecutionMode('cloud');
      setWorkspace((current) => current ?? '/workspace');
      setDiscoveringAgents(true);
      void fetchAcpAgentOptions()
        .then((options) => {
          if (cancelled) return;
          const selectedOptions = filterSelectedAcpAgents(options, appSettings.agentIds);
          const preferredCommand = preferredAgentCommand(appSettings.defaultAgent);
          setAgents(selectedOptions);
          setAgentId(
            selectedOptions.find((candidate) => candidate.command === preferredCommand)?.id ??
              selectedOptions[0]?.id ??
              '',
          );
          setAgentDiscoveryError(null);
        })
        .catch((reason: unknown) => {
          if (!cancelled) setAgentDiscoveryError(errorMessage(reason));
        })
        .finally(() => {
          if (!cancelled) setDiscoveringAgents(false);
        });
      return;
    }

    const { setPersonalWorkspaceRoot } = store.getState();
    // The machine's last folder.
    const selectedWorkspace =
      initialWorkspace ??
      localStorage.getItem(usePersonalWorkspace ? PERSONAL_WORKSPACE_KEY : WORKSPACE_KEY);
    if (selectedWorkspace !== null) {
      localStorage.setItem(WORKSPACE_KEY, selectedWorkspace);
      setWorkspace(selectedWorkspace);
    }
    void ensureNativeManagedWorkspace({ kind: 'default' })
      .then(({ rootPath }) => {
        if (cancelled) return;
        setWorkspaceProvisionError(null);
        localStorage.setItem(PERSONAL_WORKSPACE_KEY, rootPath);
        setPersonalWorkspaceRoot(rootPath);
        if (usePersonalWorkspace || selectedWorkspace === null) {
          localStorage.setItem(WORKSPACE_KEY, rootPath);
          setWorkspace(rootPath);
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled && selectedWorkspace === null) {
          setWorkspaceProvisionError(errorMessage(reason));
        }
      });
    void refreshAgents();
    void listNativeWorkspaceOpeners()
      .then((openers) => {
        if (cancelled) return;
        setWorkspaceOpeners(openers);
        setPreferredOpenerId(
          openers.some((opener) => opener.id === appSettings.editor)
            ? appSettings.editor
            : (openers[0]?.id ?? null),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setWorkspaceOpeners([]);
        setPreferredOpenerId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    appSettings.agentIds,
    appSettings.editor,
    appSettings.defaultAgent,
    initialWorkspace,
    usePersonalWorkspace,
    preview,
    previewSetup,
    refreshAgents,
    store,
  ]);

  return { refreshAgents };
}
